<template>
  <div>
    <v-progress-linear v-if="isGenerating" indeterminate></v-progress-linear>
    <div class="d-flex">
      <div class="d-flex flex-column" style="border-right: 1px solid #eee;">
        <div v-for="nav in navs" :key="nav.name">
          <v-tooltip
            max-width="600"
            location="right"
            :text="`The linked repository (${githubRepo}) is not accessible with your connected GitHub accounts.
             It may have been linked by another team member.
             You can either ask the owner for access, or reset the code view to unlink it.`"
            :disabled="!(nav.name === 'github' && isRepoInaccessible)"
          >
            <template #activator="{ props }">
              <div v-bind="props">
                <v-btn
                  :title="nav.label"
                  :class="[
                    'text-center',
                    activeNav === nav.name ? 'bg-grey-lighten-2 text-primary' : 'text-medium-emphasis'
                  ]"
                  :disabled="isLoading || (nav.name === 'github' && isRepoInaccessible)"
                  variant="elevated"
                  elevation="0"
                  @click="activeNav = nav.name"
                  style="width: 48px; height: 48px; min-width: 48px; min-height: 48px; padding: 0;"
                >
                  <v-icon>{{ nav.icon }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-tooltip>
        </div>
      </div>
      <div class="w-100 position-relative" style="height: 820px">
        <workflow-aggregates-template
          v-if="activeNav === 'template'"
          :isGenerating="isGenerating"
          :specObject="fileObjects.__spec"
          :templates="availableTemplates"
          :selectedTemplate="selectedTemplate"
          :selectedRepo="selectedRepo"
          :isCommitted="!!boundedContext.githubRepo && Object.keys(diffObjects).length === 0"
          :boundedContext="boundedContext"
          :updateApiByPrompt="updateApiByPrompt"
          :getDomainItemFinalPrompt="getDomainItemFinalPrompt"
          v-model:fileTree="fileTree"
          v-model:fileObjects="fileObjects"
          v-model:selectedAiModel="selectedAiModel"
          @updateFramework="(framework) => updateFramework(framework)"
          @updateApi="(includeBE, includeFE) => !selectedTemplate?.isLegacy ? updateApi(includeBE, includeFE) : updateApiNoTemplate()"
          @updateApiByTag="updateApiByTag"
          @clearApi="clearApi()"
          @updateUI="updateFrontend()"
          @saveFile="saveFile"
          @deleteFile="deleteFile"
          @update:object="handleObjectUpdate"
        />
        <workflow-aggregates-project
          v-if="activeNav === 'code'"
          v-model:fileTree="fileTree"
          v-model:fileObjects="fileObjects"
          :saveFile="saveFile"
          :deleteFile="deleteFile"
          :getDomainItemFinalPrompt="getDomainItemFinalPrompt"
          @update:object="handleObjectUpdate"
        />
        <workflow-aggregates-github
          v-if="activeNav === 'github'"
          :githubRepo="githubRepo"
          v-model:githubBranch="githubBranch"
          :fileObjects="fileObjects"
          :repos="repos"
          :repoObjects="repoObjects"
          :branches="branches"
          v-model:diffObjects="diffObjects"
          :createGitHubRepo="createGitHubRepo"
          :listGitHubRepos="listGitHubRepos"
          :linkGitHubRepo="linkGitHubRepo"
          :refreshGithub="checkSyncWithRepo"
          :unlinkGitHub="unlinkGitHubRepo"
          :makeCommit="makeCommit"
          :saveFile="saveFile"
        />
        <retry-generation-dialog
          v-model="showRetryDialog"
          :failedItems="failedGenerations"
          @retry="retryFailedGenerations"
        />
        <div v-if="isLoading" class="position-absolute top-0 left-0 w-100 h-100 d-flex" style="background: #eee7;">
          <div class="d-flex ma-auto justify-center align-center ga-3" style="user-select: none;">
            <v-progress-circular
              color="primary"
              size="36"
              indeterminate
            />
            <span v-html="loadingText"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { TEMPLATE_SETTINGS, LEGACY_PROJECT_TEMPLATE } from '@/codegen/constants'
import RetryGenerationDialog from '@/components/RetryGenerationDialog.vue'
import WorkflowAggregatesTemplate from '@/components/WorkflowAggregatesTemplate.vue'
import WorkflowAggregatesProject from '@/components/WorkflowAggregatesProject.vue'
import WorkflowAggregatesGithub from '@/components/WorkflowAggregatesGithub.vue'
import {
  renderTree,
  getLanguageFromFilename,
  convertWorkflowToSwaggerYaml,
  convertJsonToSwaggerYaml,
  calcGitBlobHash,
  diffArrays,
  groupAndMergeReadModels
} from '@/codegen/utils'
import gitHubApi from '@/api/gitHubApi'
import { sanitizeCardDescription } from '@/utils/sanitize'
import _ from 'lodash'
import { streamTextResponse } from '@/ai/api'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { createFileBatches } from '@/utils/batching'
import {
  AI_SYSTEM_PROMPT_UPDATE,
  AI_USER_PROMPT_ENTITY_NODEJS,
  AI_USER_PROMPT_COMMAND_NODEJS,
  AI_USER_PROMPT_READMODEL_NODEJS,
  AI_USER_PROMPT_GWT_NODEJS,
  AI_PROMPT_NODEJS_EXAMPLE_ENTITY,
  AI_PROMPT_NODEJS_EXAMPLE_COMMAND,
  AI_PROMPT_NODEJS_EXAMPLE_READMODEL,
  AI_PROMPT_NODEJS_EXAMPLE_GWT,
  AI_PROMPT_SYSTEM_FRONTEND_REACT,
  AI_PROMPT_USER_FRONTEND_REACT,
  AI_PROMPT_DATABASE_OPERATIONS,
  generateCodeMarkdown
} from '@/ai/prompts/markdown'

export default {
  components: {
    RetryGenerationDialog,
    WorkflowAggregatesTemplate,
    WorkflowAggregatesProject,
    WorkflowAggregatesGithub
  },
  props: [
    'boundedContext'
  ],
  data () {
    return {
      isLoading: false,
      loadingText: '',
      activeNav: 'template',
      navs: [
        { name: 'template', icon: 'mdi-domain', label: 'Domain Tab' },
        { name: 'code', icon: 'mdi-code-tags', label: 'Code Tab' },
        { name: 'github', icon: 'mdi-github', label: 'GitHub Tab' }
      ],
      isGenerating: false,
      isInitializing: false,
      spec: {},
      fileTree: {},
      fileObjects: {},
      selectedRepo: null,
      githubAccountLogin: this.boundedContext.githubAccountLogin || '',
      githubRepo: this.boundedContext.githubRepo || '',
      githubBranch: this.boundedContext.githubBranch || '',
      repos: [],
      repoObjects: [],
      branches: [],
      selectedBranch: '',
      diffObjects: {},
      selectedAiModel: 4,
      workflowVersion: -1,
      failedGenerations: [],
      showRetryDialog: false,
      isRepoInaccessible: false
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      savedObjects: state => state.boundedContexts.boundedContexts,
      githubAccounts: state => state.github.githubAccounts
    }),
    currentEntities () {
      return this.findEntitiesInWorkflow(this.workflow)
    },
    currentCommands () {
      return this.findCommandsInWorkflow(this.workflow, this.currentEntities)
    },
    currentReadModels () {
      return this.findReadModelsInWorkflow(this.workflow, this.currentEntities)
    },
    currentGWTs () {
      return this.findGWTsInWorkflow(this.workflow, this.currentEntities)
    },
    selectedTemplate () {
      if (this.boundedContext.templateId === LEGACY_PROJECT_TEMPLATE.name) {
        return LEGACY_PROJECT_TEMPLATE
      }

      return TEMPLATE_SETTINGS.find((item) => item.name === this.boundedContext.templateId) || null
    },
    availableTemplates () {
      if (this.selectedTemplate?.isLegacy) {
        return [LEGACY_PROJECT_TEMPLATE, ...TEMPLATE_SETTINGS]
      }

      return TEMPLATE_SETTINGS
    },
    currentTechStack () {
      const techStacks = [
        ' - Language: JavaScript ESM',
        this.selectedTemplate?.name ? ` - Framework: ${this.selectedTemplate.name}` : '',
        this.selectedTemplate?.database ? ` - Database: ${this.selectedTemplate.database}` : '',
        ' - Test Framework: Jest'
      ]
      return techStacks.filter(Boolean).join('\n')
    }
  },
  watch: {
    githubBranch (_) {
      this.checkSyncWithRepo()
    }
  },
  async mounted () {
    this.isLoading = true
    this.loadingText = 'Loading Github Repoistories ...'
    // this.githubClient = createGitHubClient()
    // this.repos = await this.githubClient.listRepos()

    if (this.savedObjects && this.savedObjects[this.boundedContext.id]) {
      this.loadingText = 'Loading Files ...'
      this.fileTree = {}
      this.addFiles(this.savedObjects[this.boundedContext.id])
      await this.loadApiSpecification()
      if (this.githubAccountLogin && this.githubRepo) {
        const [owner, repo] = this.githubRepo.split('/')
        await this.listGitHubRepos(this.githubAccountLogin)
        this.selectedRepo = this.repos.find((_repo) => _repo.name === repo)
        if (this.selectedRepo) {
          this.isRepoInaccessible = false
          await this.listRepoBranches(this.githubAccountLogin, owner, this.selectedRepo.name)
          await this.checkSyncWithRepo()
        } else {
          this.isRepoInaccessible = true
        }
      }
      this.isLoading = false
    } else if (this.boundedContext.id) {
      this.loadingText = 'Loading Files ...'
      this.$store.dispatch('boundedContexts/getObjectsFromServer', this.boundedContext.id).then(async (objects) => {
        this.fileTree = {}
        this.addFiles(objects)
        await this.loadApiSpecification()
        if (this.githubAccountLogin && this.githubRepo) {
          const [owner, repo] = this.githubRepo.split('/')
          await this.listGitHubRepos(this.githubAccountLogin)
          this.selectedRepo = this.repos.find((_repo) => _repo.name === repo)
          if (this.selectedRepo) {
            this.isRepoInaccessible = false
            await this.listRepoBranches(this.githubAccountLogin, owner, this.selectedRepo.name)
            await this.checkSyncWithRepo()
          } else {
            this.isRepoInaccessible = true
          }
        }
        this.isLoading = false
      })
    }
  },
  methods: {
    checkDiff (type) {
      switch (type) {
        case 'entity': {
          const storedEntities = this.spec.oldEntities || []
          const currentEntities = this.currentEntities || []
          return diffArrays(storedEntities, currentEntities)
        }
        case 'command': {
          const storedCommand = this.spec.oldCommands || []
          const currentCommand = this.currentCommands || []
          return diffArrays(storedCommand, currentCommand)
        }
        case 'readmodel': {
          const storedReadModel = this.spec.oldReadModels || []
          const currentReadModel = this.currentReadModels || []
          return diffArrays(storedReadModel, currentReadModel)
        }
        case 'gwt': {
          const storedGWT = this.spec.oldGWTs || []
          const currentGWT = this.currentGWTs || []
          return diffArrays(storedGWT, currentGWT)
        }
      }
    },
    findEntitiesInWorkflow (_workflow) {
      const { dataModels } = _workflow || {}
      const contextId = this.boundedContext?.id

      if (!dataModels || !contextId) {
        return []
      }

      const byId = _.keyBy(dataModels, 'id')
      const toVisit = _.map(_.filter(dataModels, ['boundedContext', contextId]), 'id')
      const visited = new Set()
      const resultIds = []

      while (toVisit.length) {
        const id = toVisit.shift()
        if (visited.has(id)) continue

        const model = byId[id]
        if (!model) continue

        visited.add(id)
        resultIds.push(id)

        // Enqueue related entities (only fields that have relatedEntityId)
        const relatedIds = _.compact(
          _.map(model.dataFields, 'relatedEntityId')
        )

        // push unseen ids
        for (const rid of relatedIds) {
          if (!visited.has(rid) && !toVisit.includes(rid) && byId[rid]) {
            toVisit.push(rid)
          }
        }
      }

      return _.map(resultIds, (id) => {
        const entity = byId[id]
        return {
          id: entity.id,
          name: entity.name,
          dataFields: _.map(entity.dataFields || [], ({ name, exampleData, cardinality, primaryKey, relatedEntityId }) => ({
            name,
            exampleData,
            cardinality,
            primaryKey,
            relatedEntityId: relatedEntityId ?? undefined
          }))
        }
      })
    },
    findCommandsInWorkflow (_workflow, _entities) {
      if (!_entities || _entities.length === 0) {
        return []
      }
      const entityIds = new Set(_entities.map((e) => e.id))

      const commands = []
      for (const event of _workflow.eventsJson) {
        const entityCard = (event.requirementsJson ?? []).find((rt) => rt.cardType.aggregateType)
        if (entityCard && entityIds.has(entityCard.entityId)) {
          const commandCard = event.requirementsJson.find((rt) => rt.cardType.triggerType)
          if (commandCard) {
            const commandDataModel = (event.dataModels || []).find((dm) => dm.cardId === commandCard.id)
            const gwtCards = cardHelpers.methods.getGwtCards(event)
            const gwtDescriptions = gwtCards.map((card) => sanitizeCardDescription(card.description))
            commands.push({
              id: commandCard.id,
              description: sanitizeCardDescription(commandCard.description),
              cardinality: commandCard.cardinality,
              dataFields: commandDataModel?.dataFields.map(field => ({ name: field.name, type: field.type ?? undefined })) || [],
              gwtDescriptions
            })
          }
        }
      }
      return commands
    },
    findReadModelsInWorkflow (_workflow, _entities) {
      if (!_entities || _entities.length === 0) {
        return []
      }
      const entityIds = new Set(_entities.map((e) => e.id))

      const allReadModels = []
      for (const event of _workflow.eventsJson) {
        const entityCard = (event.requirementsJson ?? []).find((rt) => rt.cardType.aggregateType)
        if (entityCard && entityIds.has(entityCard.entityId)) {
          const readModelCards = event.requirementsJson.filter((rt) => rt.cardType.queryType)
          for (const readModelCard of readModelCards) {
            if (!readModelCard.entityId || (readModelCard.entityId && !entityIds.has(readModelCard.entityId))) {
              continue
            }

            const readModelDataModel = (event.dataModels || []).find((dm) => dm.cardId === readModelCard.id)

            allReadModels.push({
              id: readModelCard.id,
              description: sanitizeCardDescription(readModelCard.description),
              cardinality: readModelCard.cardinality,
              entityId: readModelCard.entityId,
              dataFields: readModelDataModel?.dataFields.map((field) => ({ name: field.name, type: field.type ?? undefined })) || []
            })
          }
        }
      }

      const mergedReadModels = groupAndMergeReadModels(allReadModels, _entities)

      return mergedReadModels.map((rm) => ({
        id: rm.id,
        description: rm.description,
        cardinality: rm.cardinality,
        entityId: rm.entityId,
        dataFields: rm.dataFields,
        allIds: rm.allIds,
        allDescriptions: rm.allDescriptions
      }))
    },
    findGWTsInWorkflow (_workflow, _entities) {
      if (!_entities || _entities.length === 0) {
        return []
      }

      const entityIds = new Set(_entities.map((e) => e.id))

      const gwts = []
      for (const event of _workflow.eventsJson) {
        const entityCard = (event.requirementsJson ?? []).find((rt) => rt.cardType.aggregateType)
        if (entityCard && entityIds.has(entityCard.entityId)) {
          const gwtCards = event.requirementsJson.filter((rt) => rt.cardType.gwtType)
          for (const gwtCard of gwtCards) {
            gwts.push({
              id: gwtCard.id,
              description: sanitizeCardDescription(gwtCard.description)
            })
          }
        }
      }
      return gwts
    },
    findRelatedEntities (_entity) {
      const relatedEntities = []
      for (const field of _entity.dataFields) {
        if (field.relatedEntityId) {
          const entity = this.currentEntities.find(e => e.id === field.relatedEntityId)

          if (entity && !relatedEntities.some(e => e.id === entity.id)) {
            relatedEntities.push(entity)
          }
        }
      }

      return relatedEntities
    },
    getSwaggerDoc (type, associatedEntity, relatedEntities, refreshModel) {
      let swaggerDocs = ''
      if (type === 'entity') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [],
          readModelInfo: []
        })
      } else if (type === 'command') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [refreshModel],
          readModelInfo: []
        })
      } else if (type === 'readmodel') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [],
          readModelInfo: [refreshModel]
        })
      } else if (type === 'gwt') {
        swaggerDocs = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
      }
      return swaggerDocs
    },
    // Advanced Methods
    async loadApiSpecification (clearFiles) {
      const object = this.fileObjects.__spec
      const swagger = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)

      if (object) {
        // Getting old workflow version
        const match = object.contents.match(/version:\s*['"]?([^'"\n]+)['"]?/)
        this.workflowVersion = match ? parseInt(match[1].trim()) : -1

        if (this.workflowVersion !== this.workflow.version) {
          const oldWorkflow = await this.$store.dispatch('workflowHistory/getWorkflowHistory', {
            projectId: this.workflow.projectId,
            workflowId: this.workflow.id,
            version: this.workflowVersion
          })

          const oldEntities = this.findEntitiesInWorkflow(oldWorkflow)
          const oldCommands = this.findCommandsInWorkflow(oldWorkflow, oldEntities)
          const oldReadModels = this.findReadModelsInWorkflow(oldWorkflow, oldEntities)
          const oldGWTs = this.findGWTsInWorkflow(oldWorkflow, oldEntities)
          this.spec = {
            ...this.spec,
            oldEntities,
            oldCommands,
            oldReadModels,
            oldGWTs
          }
        }

        if (swagger !== object.contents) {
          this.fileObjects.__spec.draftContents = swagger
        }
      } else {
        this.spec = {
          name: this.boundedContext.name + ' API',
          framework: '',
          language: '',
          database: '',
          testing: '',
          oldEntities: [],
          oldCommands: [],
          oldReadModels: [],
          automationInfo: '',
          githubRepo: ''
        }

        this.fileObjects = {
          ...(!clearFiles ? this.fileObjects : {}),
          __spec: {
            filepath: '__spec',
            boundedContextId: this.boundedContext.id,
            prompt: '',
            contents: swagger,
            draftContents: null,
            tags: []
          }
        }
        this.saveFile(this.fileObjects.__spec)
      }

      // Saving domain root level prompts
      await this.saveDomainRootPrompt()
      const domainModelTypes = ['entity', 'command', 'readmodel', 'gwt']
      for (const type of domainModelTypes) {
        await this.saveDomainItemPrompt(type)
      }
    },
    async fetchAndSaveStarterFiles (repoName, repoPath, localPrefix) {
      const repoInfo = {
        login: this.githubAccounts?.length ? this.githubAccounts[0].login : 'Qlerify',
        owner: 'Qlerify',
        repoName,
        branch: 'main'
      }

      const listResponse = await gitHubApi.listGitHubRepoFiles(
        repoInfo.login,
        repoInfo.owner,
        repoInfo.repoName,
        repoPath,
        repoInfo.branch
      )

      if (!listResponse.success) {
        this.$store.commit('snackbar/showMessage', {
          content: listResponse.errorText,
          timeout: 3000,
          color: 'red'
        }, { root: true })
        return false
      }

      const filesToFetch = listResponse.resData.filter((file) => file.type === 'blob')
      if (filesToFetch.length === 0) {
        return true
      }

      const batchingInput = filesToFetch.map((file) => ({ path: file.path, type: 'blob' }))
      const fileBatches = createFileBatches(batchingInput)
      const batchPromises = fileBatches.map((batch) =>
        gitHubApi.fetchGitHubRepoFileContent(
          repoInfo.login,
          repoInfo.owner,
          repoInfo.repoName,
          repoInfo.branch,
          batch
        )
      )

      let allFetchedFiles
      try {
        const allResponses = await Promise.all(batchPromises)
        allFetchedFiles = allResponses.flatMap((response) => (response?.success ? response.resData : []))
      } catch (error) {
        this.$store.commit('snackbar/showMessage', {
          content: 'An error occurred while fetching starter project.',
          timeout: 3000,
          color: 'red'
        }, { root: true })
        return false
      }

      const savePromises = allFetchedFiles.map((file) => {
        const newFileObject = {
          filepath: file.path.replace(repoPath, localPrefix),
          boundedContextId: this.boundedContext.id,
          contents: file.content,
          tags: []
        }
        return this.saveFile(newFileObject)
      })

      await Promise.all(savePromises)
      return true
    },
    async saveDomainRootPrompt () {
      const domains = ['command', 'readmodel', 'entity', 'gwt']
      const promptTemplates = {
        entity: AI_USER_PROMPT_ENTITY_NODEJS,
        command: AI_USER_PROMPT_COMMAND_NODEJS,
        readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
        gwt: AI_USER_PROMPT_GWT_NODEJS
      }
      for (const domain of domains) {
        const domainRootPath = `root/.qlerify/${domain}-template.md`
        let domainObj = this.fileObjects[domainRootPath]
        if (!domainObj) {
          domainObj = {
            id: null,
            filepath: `root/.qlerify/${domain}-template.md`,
            boundedContextId: this.boundedContext.id,
            contents: promptTemplates[domain],
            draftContents: null,
            tags: [],
            isDirty: false
          }
          this.addFile(domainObj, true)
          this.fileObjects[domainRootPath] = domainObj
          await this.saveFile(this.fileObjects[domainRootPath])
        }
      }
    },
    async saveDomainItemPrompt (type) {
      const diffs = this.checkDiff(type)

      const promptTemplates = {
        entity: AI_USER_PROMPT_ENTITY_NODEJS,
        command: AI_USER_PROMPT_COMMAND_NODEJS,
        readmodel: AI_USER_PROMPT_READMODEL_NODEJS,
        gwt: AI_USER_PROMPT_GWT_NODEJS
      }

      for (const diff of diffs) {
        const target = diff.new || diff.old
        const normalizedDescription = (target.name || target.description)
          .toLowerCase()
          .replace(/\s+/g, '-')
        const domainItemPath = `root/.qlerify/${type}-templates/${type}-${normalizedDescription}.md`
        let domainObj = this.fileObjects[domainItemPath]
        if (!domainObj) {
          domainObj = {
            id: null,
            filepath: domainItemPath,
            boundedContextId: this.boundedContext.id,
            contents: promptTemplates[type],
            draftContents: null,
            tags: [],
            isDirty: false
          }
          this.addFile(domainObj, true)
          this.fileObjects[domainItemPath] = domainObj
          await this.saveFile(this.fileObjects[domainItemPath])
        }
      }
    },
    getDomainItemFinalPrompt (fileObject, diffData) {
      if (!fileObject && !diffData) {
        return
      }

      let targetModel
      let type
      if (fileObject) {
        const filename = fileObject.filepath.split('/').pop().replace('.md', '')
        const parts = filename.split('-')
        type = parts.shift()
        const normalizedDescription = parts.join('-')

        const domainModels = {
          entity: this.currentEntities,
          command: this.currentCommands,
          readmodel: this.currentReadModels,
          gwt: this.currentGWTs
        }
        targetModel = domainModels[type].find((item) => {
          const currentNormalized = (item.name || item.description).toLowerCase().replace(/\s+/g, '-')
          return currentNormalized === normalizedDescription
        })
      } else {
        targetModel = diffData.diff.new || diffData.diff.old
        type = diffData.type
      }

      const diffs = diffData ? [] : this.checkDiff(type)
      let diff = diffData?.diff || diffs.find((d) => (d.new || d.old).id === targetModel.id)
      if (!diff) {
        diff = { type: 'update', old: targetModel, new: targetModel }
      }

      let associatedEntity = null
      if (type === 'entity') {
        associatedEntity = targetModel
      } else {
        const eventForCard = this.workflow.eventsJson.find((event) =>
          event.requirementsJson.some((card) => card.id === targetModel.id)
        )

        if (eventForCard) {
          const entityCard = eventForCard.requirementsJson.find((card) => card.cardType.aggregateType)
          if (entityCard) {
            associatedEntity = this.currentEntities.find((entity) => entity.id === entityCard.entityId)
          }
        }
      }

      const relatedEntities = []
      for (const field of diff.new?.dataFields ?? []) {
        if (field.relatedEntityId) {
          const entity = this.currentEntities.find(e => e.id === field.relatedEntityId)

          if (entity && !relatedEntities.some(e => e.id === entity.id)) {
            relatedEntities.push(entity)
          }
        }
      }

      if (!associatedEntity) {
        return
      }

      let swaggerDocs = ''
      if (type === 'entity') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [],
          readModelInfo: []
        })
      } else if (type === 'command') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [diff.new || diff.old],
          readModelInfo: []
        })
      } else if (type === 'readmodel') {
        swaggerDocs = convertJsonToSwaggerYaml({
          ...this.spec,
          entityInfo: associatedEntity,
          relatedEntityInfo: relatedEntities,
          commandInfo: [],
          readModelInfo: [diff.new || diff.old]
        })
      } else if (type === 'gwt') {
        swaggerDocs = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
      }

      const normalizedDescription = (targetModel.name || targetModel.description)
        .toLowerCase()
        .replace(/\s+/g, '-')
      const domainItemPath = `root/.qlerify/${type}-templates/${type}-${normalizedDescription}.md`
      const promptTemplate = this.fileObjects[domainItemPath].contents

      const EXAMPLE_CODE = {
        entity: AI_PROMPT_NODEJS_EXAMPLE_ENTITY,
        command: AI_PROMPT_NODEJS_EXAMPLE_COMMAND,
        readmodel: AI_PROMPT_NODEJS_EXAMPLE_READMODEL,
        gwt: AI_PROMPT_NODEJS_EXAMPLE_GWT
      }

      const oldFiles = diff.old
        ? Object.values(this.fileObjects).filter(file => file.tags.includes(`${type}-${diff.old.id}`))
        : []

      const backendFileTree = this.fileTree.root.server ? { server: this.fileTree.root.server } : null

      return promptTemplate
        .replace('{{ TECH_STACKS }}', this.currentTechStack)
        .replace('{{ FOLDER_STRUCTURE }}', '```bash\n' + (backendFileTree ? renderTree(backendFileTree) : '') + '```')
        .replace('{{ DATABASE_OPERATIONS }}', AI_PROMPT_DATABASE_OPERATIONS)
        .replace('{{ EXAMPLE_CODE }}', EXAMPLE_CODE[type])
        .replace('{{ ORIGINAL_FILES }}', JSON.stringify(oldFiles))
        .replace('{{ LEGACY_INFO }}', JSON.stringify(diff.old, null, 2))
        .replace('{{ LATEST_INFO }}', JSON.stringify(diff.new, null, 2) +
          (associatedEntity
            ? '\n\nRelated Entity Information: \n' + JSON.stringify(associatedEntity, null, 2)
            : ''))
        .replace('{{ SWAGGER_DOCUMENT }}', swaggerDocs)
        .replace('{{ CURRENT_DATE }}', new Date().toISOString())
    },
    async updateFramework (template) {
      await this.clearApi()
      this.isLoading = true
      this.loadingText = 'Saving changes ...'

      await this.updateBoundedContext({ templateId: template.name }, 'updated template on bounded context')

      // command/entity/gwt/readmodel-template.md, __spec
      this.loadingText = 'Building backend architecture...'
      const backendSuccess = await this.fetchAndSaveStarterFiles('qlerify-starter-javascript-express', '', 'root/server/')

      if (backendSuccess) {
        this.loadingText = 'Building frontend architecture...'
        await this.fetchAndSaveStarterFiles('qlerify-starter-typescript-react', '', 'root/client/')

        this.fileObjects.__spec.draftContents = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
        await this.saveFile(this.fileObjects.__spec)
      }

      this.isLoading = false
      this.askBeforeUpdateFramework = false
    },
    async clearApi () {
      this.isLoading = true
      this.loadingText = 'Resetting API ...'

      this.repos = []
      this.spec = {
        name: this.boundedContext.name + ' API',
        framework: '',
        language: '',
        database: '',
        testing: '',
        oldEntities: [],
        oldCommands: [],
        oldReadModels: [],
        automationInfo: ''
      }
      await this.$store.dispatch('boundedContexts/deleteBoundedContext', this.boundedContext.id)

      const updatedValues = {
        templateId: null,
        githubAccountLogin: null,
        githubRepo: null,
        githubBranch: null
      }

      await this.updateBoundedContext(updatedValues, 'removed linked github account and repo from bounded context')

      this.githubAccountLogin = ''
      this.githubRepo = ''
      this.githubBranch = ''

      this.fileTree = {}
      this.fileObjects = {}

      await this.loadApiSpecification(true)
      this.isLoading = false
    },
    async updateBackend () {
      this.failedGenerations = []

      const domainModelTypes = ['entity', 'command', 'readmodel', 'gwt']
      const toVerb = (s) => s[0].toUpperCase() + s.slice(1, -1) + 'ing'
      const allDiffs = _.flatten(
        domainModelTypes.map((type) => {
          const diffs = this.checkDiff(type)
          return diffs.map((diff) => ({ type, diff }))
        })
      )

      let current = 0
      const totalDiffs = allDiffs.length
      for (const { type, diff } of allDiffs) {
        current++
        try {
          if (diff.type === 'delete') {
            this.loadingText = `${toVerb(diff.type)} code for ${type} <b>${(diff.old.name || diff.old.description)}</b> (${current}/${totalDiffs})`
            const files = Object.fromEntries(
              Object.entries(this.fileObjects).filter(
                ([_, obj]) =>
                  obj &&
                  Array.isArray(obj.tags) &&
                  obj.tags.includes(`${type}-${diff.old.id}`)
              )
            )
            for (const [filepath, obj] of Object.entries(files)) {
              const tags = Array.isArray(obj?.tags) ? obj.tags : []
              if (tags.length <= (diff.old.allIds?.length || 1)) {
                await this.deleteFile({ filepath, ...obj })
              }
            }

            continue
          }

          const target = diff.new || diff.old
          const userPrompt = this.getDomainItemFinalPrompt(null, { type, diff })

          if (userPrompt) {
            const loadingText = `${toVerb(diff.type)} code for ${type} <b>${(target.name || target.description)}</b> (${current}/${totalDiffs})`
            await this.updateApiByPrompt({ userPrompt, loadingText })
          }
        } catch (error) {
          this.failedGenerations.push({ type, diff })
        }
      }
    },
    async retryFailedGenerations () {
      this.isGenerating = true
      this.isLoading = true

      const itemsToRetry = [...this.failedGenerations]
      this.failedGenerations = []

      const toVerb = (s) => s[0].toUpperCase() + s.slice(1, -1) + 'ing'
      let current = 0
      const totalRetries = itemsToRetry.length

      for (const item of itemsToRetry) {
        const { type, diff } = item
        current++

        try {
          if (type === 'frontend') {
            this.loadingText = `Retrying <b>${diff.new.description}</b> generation... (${current}/${totalRetries})`
            await this.updateFrontend()
          } else {
            const target = diff.new || diff.old
            const userPrompt = this.getDomainItemFinalPrompt(null, { type, diff })

            if (userPrompt) {
              const loadingText = `Retrying ${toVerb(diff.type)} code for ${type} <b>${(target.name || target.description)}</b> (${current}/${totalRetries})`
              await this.updateApiByPrompt({ userPrompt, loadingText })
            }
          }
        } catch (error) {
          this.failedGenerations.push(item)
        }
      }

      this.loadingText = 'Saving OpenAPI specification ...'
      await this.saveSpecification()
      await this.saveSwaggerFile()

      this.isLoading = false
      this.isGenerating = false

      if (this.failedGenerations.length > 0) {
        this.showRetryDialog = true
      }
    },
    async updateFrontend () {
      this.isLoading = true
      this.loadingText = 'Building webpage ...'

      const legacySwaggerDoc = this.fileObjects.__spec.contents

      const clientTree = { client: this.fileTree.root.client }

      const systemPrompt = AI_PROMPT_SYSTEM_FRONTEND_REACT
      const userPrompt = AI_PROMPT_USER_FRONTEND_REACT
        .replaceAll('{{ FOLDER_STRUCTURE }}', '```bash\n' + renderTree(clientTree) + '```')
        .replaceAll('{{ LEGACY_SWAGGER_DOCUMENT }}', '')
        .replaceAll('{{ LATEST_SWAGGER_DOCUMENT }}', legacySwaggerDoc)

      await this.updateCode([systemPrompt, userPrompt], 'root/client/')

      this.isLoading = false
    },
    handleObjectUpdate (object) {
      const path = object.filepath
      this.fileObjects[path] = object
    },
    async saveSwaggerFile  () {
      const existingSwaggerFile = this.fileObjects['root/server/src/docs/swagger.yaml']
      const swaggerContent = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
      const isDifferent = !existingSwaggerFile || existingSwaggerFile.contents !== swaggerContent

      if (!isDifferent) {
        return
      }

      const swaggerObject = {
        filepath: 'root/server/src/docs/swagger.yaml',
        boundedContextId: this.boundedContext.id,
        contents: swaggerContent,
        tags: []
      }
      await this.saveFile(swaggerObject)
    },
    async updateApiByPrompt ({ userPrompt, loadingText }) {
      this.isLoading = true
      this.loadingText = loadingText || 'Generating code ...'
      await this.updateCode([AI_SYSTEM_PROMPT_UPDATE, userPrompt], 'root/server/')
      await this.saveSwaggerFile()
      this.isLoading = false
    },
    async updateApiByTag (data) {
      try {
        const { tag } = data
        const [type, ...restParts] = tag.split('-')
        const id = restParts.join('-')

        const refreshModel = [
          ...this.currentEntities,
          ...this.currentCommands,
          ...this.currentReadModels,
          ...this.currentGWTs
        ].find((domain) => domain.id === id)
        const diff = { type: 'create', new: refreshModel }

        this.isLoading = true
        this.loadingText = `Regenerating code for ${type} <b>${refreshModel.name || refreshModel.description}</b>`
        const prompt = this.getDomainItemFinalPrompt(null, { type, diff })
        await this.updateCode([AI_SYSTEM_PROMPT_UPDATE, prompt], 'root/server/')
        await this.saveSwaggerFile()
      } catch (e) {
        // Do nothing
      } finally {
        this.isLoading = false
      }
    },
    async updateApi (includeBackend = true, includeFrontend = false) {
      this.isGenerating = true
      this.isLoading = true
      this.failedGenerations = []

      if (includeBackend) {
        try {
          await this.updateBackend()
        } catch (e) {
          // Do nothing
        }
      }

      if (includeFrontend) {
        try {
          await this.updateFrontend()
        } catch (e) {
          this.failedGenerations.push({
            type: 'frontend',
            diff: { new: { description: 'React Application' } }
          })
        }
      }

      this.loadingText = 'Saving OpenAPI specification ...'
      await this.saveSpecification()
      await this.saveSwaggerFile()
      await this.checkSyncWithRepo()

      this.isLoading = false
      this.isGenerating = false

      if (this.failedGenerations.length > 0) {
        this.showRetryDialog = true
      }
    },
    async saveSpecification () {
      const specFile = this.fileObjects.__spec
      this.spec = {
        ...this.spec,
        oldEntities: this.currentEntities,
        oldCommands: this.currentCommands,
        oldReadModels: this.currentReadModels,
        oldGWTs: this.currentGWTs
      }

      specFile.contents = convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
      specFile.draftContents = null
      await this.saveFile(specFile)
    },
    addFile (object, openDirectory = false) {
      const parts = object.filepath.split('/')

      if (object.filepath !== '__spec') {
        let curr = this.fileTree
        let currPath = ''
        parts.forEach(part => {
          if (!curr[part]) {
            curr[part] = {}
          }
          curr = curr[part]

          // make sure new directories are opened
          if (openDirectory && !part.includes('.')) {
            if (currPath) {
              currPath += '/'
            }
            currPath += part
          }
        })
      }

      const objectCopy = { ...object }
      if (!objectCopy.contents) {
        objectCopy.contents = ''
      }
      this.fileObjects[object.filepath] = objectCopy
    },
    addFiles (objects) {
      objects.forEach(object => {
        this.addFile(object, true)
      })
    },
    async updateCode (prompts, pathPrefix = '') {
      let buffer = ''
      let currentFilepath = null
      let currentIsDeleted = false

      const fileHeaderRegex = /=== FILE: (.+?)( \(deleted\))? ===\s*/g
      await streamTextResponse({
        messages: [{ role: 'system', content: prompts[0] }, { role: 'user', content: prompts[1] }],
        aiVersion: this.selectedAiModel,
        onChunkReceived: async (chunkResponse) => {
          buffer += chunkResponse

          let match
          while ((match = fileHeaderRegex.exec(buffer)) !== null) {
            const newFilepath = match[1].trim()
            const newIsDeleted = !!match[2]

            const splitIndex = match.index
            const previousContent = buffer.slice(0, splitIndex)

            if (currentFilepath) {
              if (currentIsDeleted) {
                const deletedObject = this.fileObjects[currentFilepath]
                if (deletedObject) {
                  await this.deleteFile(deletedObject)
                }
              } else {
                await this.updateFile(currentFilepath, pathPrefix, previousContent)
              }
            }

            currentFilepath = newFilepath
            currentIsDeleted = newIsDeleted
            buffer = buffer.slice(fileHeaderRegex.lastIndex)
            fileHeaderRegex.lastIndex = 0
          }
        }
      })

      if (currentFilepath && buffer.trim()) {
        if (currentIsDeleted) {
          const deletedObject = this.fileObjects[currentFilepath]
          if (deletedObject) {
            await this.deleteFile(deletedObject)
          }
        } else {
          await this.updateFile(currentFilepath, pathPrefix, buffer)
        }
      }
    },
    updateFileAndCapture (_filepath, pathPrefix, rawContent) {
      let processedContent = rawContent
      const tagMatch = processedContent.match(/=== TAG: (.+?) ===/)
      let tags = []

      if (tagMatch) {
        tags = tagMatch[1].split(',').map((tag) => tag.trim())
        processedContent = processedContent.replace(/=== TAG: .+? ===\s*/, '')
      }

      const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/
      const codeMatch = processedContent.match(codeBlockRegex)

      const extractedContent = codeMatch ? codeMatch[1] : processedContent
      const trimmedContent = extractedContent.trimEnd()
      const finalContent = trimmedContent + '\n'

      const filepath = _filepath.startsWith(pathPrefix) ? _filepath : pathPrefix + _filepath

      return { filepath, content: finalContent, tags }
    },
    async updateCodeNoTemplate (prompt, pathPrefix = '') {
      const modifiedFilesMap = new Map()

      let buffer = ''
      let currentFilepath = null
      let currentIsDeleted = false
      const fileHeaderRegex = /=== FILE: (.+?)( \(deleted\))? ===\s*/g

      try {
        await streamTextResponse({
          messages: [{ role: 'user', content: prompt }],
          aiVersion: this.selectedAiModel,
          onChunkReceived: async (chunkResponse) => {
            buffer += chunkResponse
            let match
            while ((match = fileHeaderRegex.exec(buffer)) !== null) {
              const newFilepath = match[1].trim()
              const newIsDeleted = !!match[2]
              const splitIndex = match.index
              const previousContent = buffer.slice(0, splitIndex)

              if (currentFilepath) {
                if (currentIsDeleted) {
                  const deletedObject = this.fileObjects[currentFilepath]
                  deletedObject && await this.deleteFile(deletedObject)
                } else {
                  const { filepath, content, tags } = this.updateFileAndCapture(currentFilepath, pathPrefix, previousContent)
                  modifiedFilesMap.set(filepath, { content, tags })
                }
              }
              currentFilepath = newFilepath
              currentIsDeleted = newIsDeleted
              buffer = buffer.slice(fileHeaderRegex.lastIndex)
              fileHeaderRegex.lastIndex = 0
            }
          }
        })

        if (currentFilepath && buffer.trim()) {
          const { filepath, content, tags } = this.updateFileAndCapture(currentFilepath, pathPrefix, buffer)
          modifiedFilesMap.set(filepath, { content, tags })
        }

        return modifiedFilesMap
      } catch (e) {
        return {}
      }
    },
    async handleUpdateEachDomainNoTemplate (type, diff, baseFileObjectsMap) {
      const resultingStateMap = new Map(safeStructuredClone(Array.from(baseFileObjectsMap)))

      const promptTemplate = generateCodeMarkdown

      const relevantFiles = []
      if (diff.old) {
        for (const [filepath, fileObject] of baseFileObjectsMap.entries()) {
          if (fileObject.tags && fileObject.tags.some((tag) => tag === `${type}-${diff.old.id}`)) {
            relevantFiles.push({ ...fileObject, filepath })
          }
        }
      }

      const filesString = relevantFiles.length > 0
        ? relevantFiles.map((file) => {
          let text = '=== File: ' + file.filepath + ' ===\n'
          text += '=== TAG: ' + file.tags.join(', ') + ' ===\n'
          text += '```' + getLanguageFromFilename(file.filepath) + '\n'
          text += file.contents
          text += '\n```\n'
          return text
        }).join('')
        : 'NO FILES'

      const gwtDescriptions = diff.new?.gwtDescriptions || []

      const updatePrompt = promptTemplate
        .replaceAll('{{ PROMPT_TYPE }}', type)
        .replace('{{ FOLDER_STRUCTURE }}', '```bash\n' + renderTree(this.fileTree) + '```')
        .replace('{{ ORIGINAL_FILES }}', filesString)
        .replace('{{ LEGACY_INFO }}', JSON.stringify(diff.old, null, 2))
        .replace('{{ LATEST_INFO }}', JSON.stringify(diff.new, null, 2))
        .replace('{{ GWT_DESCRIPTIONS }}', JSON.stringify(gwtDescriptions, null, 2))

      const modifiedFilesMap = await this.updateCodeNoTemplate(updatePrompt, 'root/')

      for (const [path, modifiedFile] of modifiedFilesMap.entries()) {
        if (resultingStateMap.has(path)) {
          const existingFile = resultingStateMap.get(path)
          existingFile.contents = modifiedFile.content
          existingFile.tags = modifiedFile.tags
        } else {
          resultingStateMap.set(path, {
            id: null,
            filepath: path,
            boundedContextId: this.boundedContext.id,
            contents: modifiedFile.content,
            draftContents: null,
            tags: modifiedFile.tags || [],
            isDirty: false
          })
        }
      }

      return resultingStateMap
    },
    async updateApiNoTemplate () {
      this.isGenerating = true
      this.isLoading = true

      let codeStateMap = new Map(Object.entries(this.fileObjects))

      const domainModelTypes = ['entity', 'command', 'readmodel']
      const toVerb = (s) => s[0].toUpperCase() + s.slice(1, -1) + 'ing'

      for (const type of domainModelTypes) {
        const diffs = this.checkDiff(type)
        if (diffs.length > 0) {
          this.loadingText = `Processing ${type} changes...`
          for (const diff of diffs) {
            const target = diff.new || diff.old
            this.loadingText = `${toVerb(diff.type)} ${type}: <b>${target.name || target.description}</b>`

            codeStateMap = await this.handleUpdateEachDomainNoTemplate(type, diff, codeStateMap)
          }
        }
      }

      const finalCodeStateMap = codeStateMap

      this.loadingText = 'Saving Files...'

      for (const [path, modifiedFile] of finalCodeStateMap.entries()) {
        const originalFile = this.fileObjects[path]

        if (originalFile) {
          const contentChanged = originalFile.contents !== modifiedFile.contents
          const tagsChanged = JSON.stringify(originalFile.tags || []) !== JSON.stringify(modifiedFile.tags || [])

          if (contentChanged || tagsChanged) {
            originalFile.draftContents = modifiedFile.contents
            originalFile.tags = modifiedFile.tags || []
            originalFile.isDirty = true
            await this.saveFile(originalFile)
          }
        } else {
          const newFileObject = {
            id: null,
            filepath: path,
            boundedContextId: this.boundedContext.id,
            contents: '',
            draftContents: modifiedFile.contents,
            tags: modifiedFile.tags || [],
            isDirty: true
          }
          this.addFile(newFileObject, true)
          this.fileObjects[path] = newFileObject
          await this.saveFile(this.fileObjects[path])
        }
      }

      await this.saveSpecification()

      this.isLoading = false
      this.isGenerating = false
    },
    async updateFile (_filepath, pathPrefix, rawContent) {
      let diffContents = rawContent.trim()
      diffContents = diffContents.replace(/```[\w]*\n?|```/g, '').trim()
      const match = diffContents.match(/=== TAG: (.+?) ===/)
      let tags = []
      if (match) {
        tags = match[1].split(',').map(tag => tag.trim())
      }
      diffContents = diffContents.replace(/=== TAG: .+? ===\s*/, '')

      const filepath = _filepath.startsWith(pathPrefix) ? _filepath : pathPrefix + _filepath

      const shouldCreate = !this.fileObjects[filepath]
      // const updatedContents = applyGitDiff(this.fileObjects[filepath]?.contents || '', diffContents)
      const updatedContents = diffContents

      let updateObject = _.cloneDeep(this.fileObjects[filepath] || {})
      if (shouldCreate) {
        updateObject = {
          filepath,
          boundedContextId: this.boundedContext.id,
          contents: '',
          draftContents: updatedContents,
          tags
        }
      } else {
        updateObject = { ...updateObject, draftContents: updatedContents, tags }
        // updateObject = { ...updateObject, contents: updatedContents }
      }
      await this.saveFile(updateObject)
    },
    async updateBoundedContext (updatedValues, updateNote) {
      const newBoundedContexts = this.workflow.boundedContexts?.map((item) => {
        if (item.id !== this.boundedContext.id) {
          return item
        }

        return { ...item, ...updatedValues }
      })

      return await this.$store.dispatch('workflow/updateWorkflow', {
        projectId: this.workflow.projectId,
        input: {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          boundedContexts: newBoundedContexts,
          updateNote: {
            action: updateNote,
            target: ''
          }
        }
      })
    },
    async saveFile (object) {
      const parts = object.filepath.split('/')

      if (!object.id) {
        this.addFile(object, true)
        this.fileObjects[object.filepath] = await this.$store.dispatch('boundedContexts/createObject', object)
      } else {
        const files = this.savedObjects[this.boundedContext.id]
        const originalFile = files ? files.find((f) => f.id === object.id) : null
        const initialContent = originalFile?.contents || ''

        this.fileObjects[object.filepath] = object
        await this.$store.dispatch('boundedContexts/updateObjectContents', object)

        const rootTemplateRegex = /^root\/\.qlerify\/(command|entity|readmodel|gwt)-template\.md$/
        const match = object.filepath.match(rootTemplateRegex)

        if (match) {
          const type = match[1]
          const newItemTemplateContent = object.contents
          const itemTemplatePrefix = `root/.qlerify/${type}-`

          const updatePromises = Object.values(this.fileObjects)
            .filter((file) => {
              const isItemTemplate = file.filepath.startsWith(itemTemplatePrefix) && file.filepath !== object.filepath
              const isUnmodified = file.contents === initialContent

              return isItemTemplate && isUnmodified
            })
            .map((fileToUpdate) => {
              const updatedFile = { ...fileToUpdate, contents: newItemTemplateContent }
              this.fileObjects[fileToUpdate.filepath] = updatedFile

              return this.$store.dispatch('boundedContexts/updateObjectContents', updatedFile)
            })

          if (updatePromises.length > 0) {
            await Promise.all(updatePromises)
          }
        }

        if (object.filepath === '__spec') {
          this.$store.commit('snackbar/showMessage', { content: 'Saved template information', timeout: 3000, color: 'blue' }, { root: true })
        } else {
          this.$store.commit('snackbar/showMessage', { content: 'Saved ' + parts[parts.length - 1], timeout: 3000, color: 'blue' }, { root: true })
        }
      }
      object.isDirty = false
    },
    async deleteFile (object) {
      if (object.id) {
        await this.$store.dispatch('boundedContexts/deleteObject', object)
        const path = object.realpath || object.filepath

        const keys = path.split('/')
        const lastKey = keys.pop()
        let parent = this.fileTree
        for (const key of keys) {
          if (parent[key] === undefined) {
            return
          }
          parent = parent[key]
        }
        delete parent[lastKey]
        delete this.fileObjects[path]
        this.$store.commit('snackbar/showMessage', { content: 'Deleted ' + lastKey, timeout: 3000, color: 'blue' }, { root: true })
      }
    },
    // GitHub-Related Functions
    async createGitHubRepo (login, repo, description, isPrivate) {
      const response = await gitHubApi.createGitHubRepo(login, repo, description, isPrivate)
      if (response.success) {
        const newRepo = response.resData
        this.repos = [...this.repos, newRepo]
        this.$store.commit('snackbar/showMessage', { content: newRepo.name + ' Repository has been created successfully', timeout: 3000, color: 'green' }, { root: true })
        await this.initGitHubRepo(login, repo, 'main')
        // Account owner (login) acts as a repo owner here, so login is presented 2 times
        await this.linkGitHubRepo(login, login, repo)
      } else {
        this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    },
    async initGitHubRepo (login, repo, branch) {
      // Account owner (login) acts as a repo owner here, so login is presented 2 times
      const response = await gitHubApi.initGitHubRepo(login, login, repo, branch, '.gitkeep', '', 'feat: init repo')
      if (response.success) {
        this.$store.commit('snackbar/showMessage', { content: 'Repository has been initialized successfully', timeout: 3000, color: 'green' }, { root: true })
      } else {
        this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    },
    async listGitHubRepos (login) {
      const response = await gitHubApi.listGitHubRepos(login)
      if (response.success) {
        this.repos = response.resData
      } else if (!response.errorText?.includes('token not found for login')) {
        this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    },
    async listRepoBranches (login, owner, repo) {
      const response = await gitHubApi.listRepoBranches(login, owner, repo)
      if (response.success) {
        this.branches = response.resData
      } else {
        this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
      }
    },
    async linkGitHubRepo (login, owner, repo) {
      let linkedRepo = null
      try {
        linkedRepo = this.repos.find(r => r.full_name === owner + '/' + repo)
      } catch (err) {
        this.$store.commit('snackbar/showMessage', { content: 'Repository creation failed.', timeout: 3000, color: 'red' }, { root: true })
        return
      }

      this.isLoading = true
      this.loadingText = 'Linking with Github repositories ...'

      // Save github repo information to db
      this.githubAccountLogin = login
      this.githubRepo = linkedRepo.full_name

      const updatedValues = {
        githubAccountLogin: login,
        githubRepo: linkedRepo.full_name
      }
      await this.updateBoundedContext(updatedValues, 'linked repository')
      this.fileObjects.__spec = {
        ...this.fileObjects.__spec,
        contents: convertWorkflowToSwaggerYaml(this.workflow, this.boundedContext)
      }
      await this.saveFile(this.fileObjects.__spec)
      this.selectedRepo = linkedRepo

      // Getting list of branches
      await this.listRepoBranches(login, owner, this.selectedRepo.name)
      this.githubBranch =
        this.branches.find(b => b.name === 'main') ||
        this.branches.find(b => b.name === 'master') ||
        (this.branches.length ? this.branches[0] : null)
      await this.updateBoundedContext({
        githubBranch: typeof this.githubBranch === 'string' ? this.githubBranch : this.githubBranch?.name
      }, 'linked branch')

      this.isLoading = false
      await this.checkSyncWithRepo()
    },
    async checkSyncWithRepo () {
      if (!this.selectedRepo || !Object.keys(this.selectedRepo).length || !this.githubAccountLogin) {
        return
      }

      this.isLoading = true
      this.loadingText = 'Comparing your code with GitHub...'

      const listResponse = await gitHubApi.listGitHubRepoFiles(
        this.githubAccountLogin,
        this.selectedRepo.owner.login,
        this.selectedRepo.name,
        null,
        typeof this.githubBranch === 'string' ? this.githubBranch : this.githubBranch?.name
      )

      if (!listResponse.success) {
        this.$store.commit('snackbar/showMessage', {
          content: listResponse.errorText,
          timeout: 3000,
          color: 'red'
        }, { root: true })
        this.isLoading = false
        return
      }

      const githubMap = new Map(
        listResponse.resData
          .filter((file) => file.type === 'blob')
          .map((file) => [file.path, file])
      )

      const currentMap = new Map(
        Object.entries(this.fileObjects)
          .filter(([path]) => path !== '__spec')
          .map(([path, file]) => [path.replace('root/', ''), file.contents])
      )

      const filesToFetchContentFor = []
      const allPaths = new Set([...currentMap.keys(), ...githubMap.keys()])
      const diffsToProcess = []

      for (const path of allPaths) {
        const localContent = currentMap.get(path)
        const remoteFile = githubMap.get(path)
        const hasLocal = currentMap.has(path)
        const hasRemote = githubMap.has(path)

        if (hasLocal && !hasRemote) {
          diffsToProcess.push({ path, type: 'added', newContent: localContent })
        } else if (!hasLocal && hasRemote) {
          diffsToProcess.push({ path, type: 'deleted' })
          filesToFetchContentFor.push(remoteFile)
        } else if (hasLocal && hasRemote) {
          const normalizedLocalContent = (localContent || '').replace(/\r\n/g, '\n')
          const localByteLength = new TextEncoder().encode(normalizedLocalContent).length

          if (localByteLength !== remoteFile.size || calcGitBlobHash(normalizedLocalContent) !== remoteFile.sha) {
            diffsToProcess.push({ path, type: 'modified', newContent: localContent })
            filesToFetchContentFor.push(remoteFile)
          }
        }
      }

      let fetchedContentMap = new Map()
      if (filesToFetchContentFor.length > 0) {
        const repoInfo = {
          login: this.githubAccountLogin,
          owner: this.selectedRepo.owner.login,
          repoName: this.selectedRepo.name,
          branch: typeof this.githubBranch === 'string' ? this.githubBranch : this.githubBranch?.name
        }

        const batchingInput = filesToFetchContentFor.map((file) => ({ path: file.path, type: 'blob' }))
        const fileBatches = createFileBatches(batchingInput)
        const batchPromises = fileBatches.map((batch) =>
          gitHubApi.fetchGitHubRepoFileContent(
            repoInfo.login,
            repoInfo.owner,
            repoInfo.repoName,
            repoInfo.branch,
            batch
          )
        )

        try {
          const allResponses = await Promise.all(batchPromises)
          const allFetchedFiles = allResponses.flatMap((response) => (response?.success ? response.resData : []))
          fetchedContentMap = new Map(allFetchedFiles.map((file) => [file.path, file.content]))
        } catch (error) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Error fetching file contents from GitHub.',
            timeout: 3000,
            color: 'red'
          }, { root: true })
          this.isLoading = false
          return
        }
      }

      const finalDiffs = {}
      for (const diff of diffsToProcess) {
        const oldContent = fetchedContentMap.get(diff.path) || ''
        finalDiffs[diff.path] = {
          filepath: diff.path,
          githubContents: diff.type === 'added' ? null : oldContent,
          originalContents: diff.type === 'deleted' ? null : diff.newContent,
          userUpdatedContents: diff.type === 'deleted' ? null : diff.newContent,
          aiUpdatedContents: this.fileObjects[`root/${diff.path}`]?.draftContents || undefined,
          boundedContextId: '',
          id: '',
          prompt: '',
          tags: []
        }
      }

      this.diffObjects = Object.keys(finalDiffs)
        .sort()
        .reduce((acc, key) => {
          acc[key] = finalDiffs[key]
          return acc
        }, {})

      this.isLoading = false
    },
    async makeCommit (commitMessage) {
      if (!this.selectedRepo) return

      const updates = Object.entries(this.diffObjects).map(([path, file]) => {
        const oldContent = file.githubContents
        const newContent = file.originalContents

        let type
        if (oldContent === null && newContent !== null) type = 'added'
        else if (oldContent !== null && newContent === null) type = 'deleted'
        else if (oldContent !== newContent) type = 'modified'
        else return null // no change

        const update = { type, path: path.replace('root/', '') }

        if (type !== 'deleted') {
          update.content = newContent
        }

        return update
      }).filter(Boolean)

      if (updates.length) {
        const response = await gitHubApi.commitGitHubRepoFiles(
          this.githubAccountLogin,
          this.selectedRepo.owner.login,
          this.selectedRepo.name,
          'main',
          updates,
          commitMessage
        )

        if (response.success) {
          this.$store.commit('snackbar/showMessage', { content: updates.length + ' file(s) have been commited successfuly', timeout: 3000, color: 'blue' }, { root: true })
        } else {
          this.$store.commit('snackbar/showMessage', { content: response.errorText, timeout: 3000, color: 'red' }, { root: true })
        }
      }

      this.commitMessage = ''
      this.diffObjects = {}
    },
    async unlinkGitHubRepo () {
      this.githubAccountLogin = ''
      this.githubRepo = ''
      this.githubBranch = ''
      this.selectedRepo = null
      this.repos = []

      const updatedValues = {
        githubAccountLogin: null,
        githubRepo: null,
        githubBranch: null
      }
      await this.updateBoundedContext(updatedValues, 'unlinked GitHub repository from bounded context')
    }
  }
}
</script>

<style scoped>
</style>
